import { call, put } from 'redux-saga/effects';
import { push } from 'redux-first-history';
import actions from '../redux/auth/actions';
import { updateLastVisitedRoute } from './utility';
import { ErrorWithMessage, logger } from './logger';
import { t } from '../i18n';
import requestActions from '../redux/requestStatus/actions';
import notificationsActions from '../redux/notifications/actions';

const HTTP_FORBIDDEN = 403;

// `checkData` throws `403:<sentence>` when a message is supplied, bare `403` otherwise.
const forbiddenMessage = (message: string): string | undefined => {
  const [, explanation] = message.split(/^403:/);

  return explanation?.trim() || undefined;
};

/**
 * An `ApiInstance` (axios) request never rejects: `parseError` resolves a failed call to
 * `{ status, messages?, error?, data? }`, so `sendRequest` hands it back untouched. This is the sentence to show
 * for such a result when it carries no field validations: the server's own, else one for the status.
 */
export const resolvedErrorMessage = (
  result: { status?: number; messages?: string[]; error?: string } | undefined,
): string =>
  result?.messages?.join(' ') ||
  result?.error ||
  (result?.status === HTTP_FORBIDDEN
    ? t('dash.common.notification.forbidden_error')
    : t('dash.common.notification.something_went_wrong'));

// A validations map is keyed by field and holds message arrays or nested maps; a resolved error object
// (`{ status, messages }`) carries scalars too, so it never passes.
const isValidationsMap = (value: unknown): value is Record<string, unknown> =>
  !!value &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  Object.keys(value).length > 0 &&
  Object.values(value).every(entry => Array.isArray(entry) || (!!entry && typeof entry === 'object'));

/**
 * The field validations a failed save answered with, whichever transport served it. A fetch-based `Api` call
 * rejects and `sendRequest` returns the bare validations map it carried (helpers/request.ts puts it on
 * `err.response`); an `ApiInstance` (axios) call resolves `{ status, messages?, data? }` and keeps the map under
 * `data`. Anything else (a refused write, a plain failure) is not a validations map and yields nothing.
 */
export const fieldValidations = (result: any): Record<string, unknown> | undefined => {
  const nested = result?.data?.errors?.data?.validations ?? result?.data?.validations;

  if (nested) {
    return nested;
  }

  return isValidationsMap(result) ? result : undefined;
};

export function* sendRequest(
  request: (data: any) => Promise<any>,
  data: any,
  errorType?: string,
  sendingRequest?: string,
  redirectToNotFound?: boolean,
  options?: any,
): Generator<any, any, any> {
  yield put(requestActions.setPendingToTrue());

  if (sendingRequest) {
    yield put({ type: sendingRequest, sending: true });
  }
  try {
    return yield call(request, data);
  } catch (error) {
    const err = error as ErrorWithMessage & { code?: number };

    if (err && err.message.includes('401') && !options?.ignoreLogout) {
      logger.warn(err);

      yield put({ type: actions.LOGOUT_SUCCESS });
      yield updateLastVisitedRoute(null);
    } else if (err.message.includes('403')) {
      logger.warn(err);

      if (err.message.includes('not allowed')) {
        yield put(push('/forbidden'));
      } else if (options?.preserveErrorStatus) {
        // A 403 can refuse an otherwise-permitted write, so the generic forbidden toast would
        // mislead; hand the status and message to the caller instead.
        return { status: HTTP_FORBIDDEN, message: forbiddenMessage(err.message) };
      } else {
        yield put(
          notificationsActions.requestError({
            error: t('dash.common.notification.forbidden_error'),
          }),
        );
      }
    } else if (redirectToNotFound && err.message.includes('404')) {
      logger.warn(err);

      yield put(push('/404'));
    } else if (!err.response && errorType && !options?.ignoreLogout) {
      logger.error(err);

      yield put({
        type: errorType,
        payload: {
          id: new Date().getTime(),
          error: err.message,
        },
      });
    } else {
      logger.error(err);

      // A validation error carries only the bare validations map on `err.response` (see
      // helpers/request.ts) while the HTTP status lives on `err.code`. `preserveErrorStatus`
      // returns both to the caller without changing the legacy shape for everyone else.
      if (options?.preserveErrorStatus) {
        return { status: err.code, validations: err.response };
      }

      return err.response;
    }
  } finally {
    if (sendingRequest) {
      yield put({ type: sendingRequest, sending: false });
    }
    yield put(requestActions.setPendingToFalse());
  }
}
